// Wyoming Order detail. Two sections:
//   - Working interests: rows with record_class='working_interest'.
//     Carries interest_type + decimal + raw description.
//   - Broad roster: interested_party rows. Plain name + tract.
//
// The broad roster comes from the Vision PDF parse (or CSV);
// working interests come from CSV import or the manual add-lead
// form, which is the primary entry path here.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { wyApi, type WyOrderDetailResponse, type WyOwnershipRow } from '../services/wyApi';
import { CopyButton } from '../components/CopyButton';
import { PushedIndicator } from '../components/PushedIndicator';
import { LeadListIndicator } from '../components/LeadListIndicator';
import { SkipMarker } from '../components/SkipMarker';
import { HasContactIndicator } from '../components/HasContactIndicator';

export function WyOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<WyOrderDetailResponse | null>(null);
  const [err, setErr]   = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [addOpen, setAddOpen]   = useState(false);

  async function reload() {
    if (!id) return;
    try { setData(await wyApi.getOrder(id)); setErr(null); }
    catch (e: any) { setErr(e?.message ?? 'failed to load'); }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  async function retry() {
    if (!id) return;
    setRetrying(true);
    try { await wyApi.retryParse(id); await reload(); }
    catch (e: any) { setErr(e?.message ?? 'retry failed'); }
    finally { setRetrying(false); }
  }

  if (err) return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto">
      <Link to="/mi/wy/orders" className="text-[12px] text-text-faint font-mono hover:text-text-dim">← all Wyoming orders</Link>
      <div className="mt-4 text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div>
    </div>
  );
  if (!data) return <div className="px-8 py-7 text-[12px] text-text-faint font-mono">loading…</div>;
  const o = data.wy_order;
  const trsLabel = o.trs.map(t => `T${t.township_num}${t.township_dir} R${t.range_num}${t.range_dir} Sec ${t.section}`).join(' · ');

  return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto">
      <Link to="/mi/wy/orders" className="text-[12px] text-text-faint font-mono hover:text-text-dim">← all Wyoming orders</Link>
      <header className="mt-2 flex items-end justify-between pb-6 border-b border-border">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">wyoming · order</div>
          <div className="font-display text-[28px] leading-tight">
            <span className="text-text-dim">WOGCC</span> <em className="text-accent not-italic">{o.order_number}</em>
            {o.operator && <span className="text-text-dim text-[16px] ml-3">{o.operator}</span>}
          </div>
          <div className="text-[12px] text-text-dim mt-2 font-mono space-x-2">
            <span>{o.county ?? 'no county'}</span>
            <span className="text-text-ghost">·</span>
            <span>{trsLabel || 'no TRS'}</span>
            <span className="text-text-ghost">·</span>
            <span>{o.filed_date ? `filed ${o.filed_date}` : 'filed —'}</span>
            <span className="text-text-ghost">·</span>
            <span>{data.counts.ownership_total} rows · {data.counts.working_interest} WI</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={retry} disabled={retrying}
            className="px-3 py-2 text-[12px] text-text-dim border border-border rounded hover:text-text hover:bg-bg-hover disabled:opacity-50">
            {retrying ? 'Re-parsing…' : '↻ Re-parse schedule'}
          </button>
          <button onClick={() => setAddOpen(true)}
            className="px-3 py-2 text-[12px] bg-accent text-bg font-medium rounded hover:bg-accent-bright">
            + Add lead
          </button>
        </div>
      </header>

      {o.parse_warnings.length > 0 && (
        <div className="mt-4 bg-bg-card-soft border border-border rounded px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-1">parse log</div>
          <pre className="text-[10px] text-text-dim font-mono whitespace-pre-wrap">{o.parse_warnings.join('\n')}</pre>
        </div>
      )}

      <Section title="Working interests" rows={data.working_interests} />
      <Section title="Broad roster" rows={data.interested_parties} />

      {addOpen && id && (
        <WyAddLeadModal wyOrderId={id} defaultTrs={o.trs[0] ?? null}
          onClose={() => setAddOpen(false)}
          onAdded={async () => { setAddOpen(false); await reload(); }} />
      )}
    </div>
  );
}

function Section({ title, rows }: { title: string; rows: WyOwnershipRow[] }) {
  return (
    <section className="mt-6">
      <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-2">
        {title} <span className="text-text-faint ml-1">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="bg-bg-card-soft border border-border rounded px-4 py-4 text-[12px] text-text-faint italic">
          none yet
        </div>
      ) : (
        <div className="bg-bg-card-soft border border-border rounded overflow-hidden">
          <div className="grid grid-cols-[2fr_140px_110px_90px_120px_36px] gap-3 px-4 py-2 bg-bg-input/40 border-b border-border text-[10px] uppercase tracking-wider font-mono text-text-faint">
            <div>Party</div>
            <div>Tract</div>
            <div>Interest</div>
            <div className="text-right">Decimal</div>
            <div>Source / raw</div>
            <div></div>
          </div>
          {rows.map(r => (
            <div key={r.id}
              className="grid grid-cols-[2fr_140px_110px_90px_120px_36px] gap-3 items-center px-4 py-2 border-b border-border last:border-b-0 hover:bg-bg-hover/30">
              <div className="min-w-0">
                <Link to={`/mi/party/${r.party_id}`}
                  className={'text-[13px] hover:text-accent ' + (r.skipped_at ? 'text-text-faint line-through' : 'text-text')}>
                  {r.display_name_natural ?? r.display_name}
                </Link>
                <PushedIndicator   pushedAt={r.last_pushed_to_close_at} className="ml-1.5" />
                <LeadListIndicator status={null /* WY add to lead-list pill comes via add-to-list flow */} className="ml-1.5" />
                <SkipMarker        skippedAt={r.skipped_at} className="ml-1.5" />
                <HasContactIndicator hasPhone={r.has_phone} hasAddress={r.has_address} className="ml-1.5 align-middle" />
                <div className="text-[10px] text-text-faint font-mono mt-0.5">
                  T{r.township_num}{r.township_dir} R{r.range_num}{r.range_dir} Sec {r.section}
                  {(r.skip_trace_city || r.skip_trace_state) && (
                    <span className="ml-2">{[r.skip_trace_city, r.skip_trace_state].filter(Boolean).join(', ')}</span>
                  )}
                </div>
              </div>
              <div className="text-[11px] text-text-dim font-mono truncate" title={r.tract_description ?? undefined}>
                {r.tract_description ?? '—'}
              </div>
              <div className="text-[11px] text-text font-mono">{r.interest_type ?? '—'}</div>
              <div className="text-[11px] text-text-dim font-mono text-right">
                {r.decimal_interest !== null ? r.decimal_interest.toFixed(8) : '—'}
              </div>
              <div className="text-[10px] text-text-faint font-mono truncate" title={r.interest_description_raw ?? undefined}>
                {r.source}
                {r.interest_description_raw && <span className="ml-1">· {r.interest_description_raw}</span>}
              </div>
              <div className="flex justify-end">
                <CopyButton text={r.display_name_natural ?? r.display_name}
                  title={`Copy "${r.display_name_natural ?? r.display_name}" for TruePeopleSearch`}
                  tabIndex={-1} />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// Add-lead modal — primary working-interest entry path.
function WyAddLeadModal(props: {
  wyOrderId: string;
  defaultTrs: { township_num: number; township_dir: 'N'|'S'; range_num: number; range_dir: 'E'|'W'; section: number } | null;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [tn, setTn] = useState(String(props.defaultTrs?.township_num ?? ''));
  const [td, setTd] = useState<'N'|'S'>(props.defaultTrs?.township_dir ?? 'N');
  const [rn, setRn] = useState(String(props.defaultTrs?.range_num ?? ''));
  const [rd, setRd] = useState<'E'|'W'>(props.defaultTrs?.range_dir ?? 'W');
  const [sec, setSec] = useState(String(props.defaultTrs?.section ?? ''));
  const [recordClass, setRecordClass] = useState<'working_interest' | 'interested_party'>('working_interest');
  const [tractDescription, setTractDescription] = useState('');
  const [interestDescription, setInterestDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setErr(null);
    if (!name.trim()) { setErr('name required'); return; }
    const trsParsed = {
      township_num: parseInt(tn, 10), township_dir: td,
      range_num:    parseInt(rn, 10), range_dir:    rd,
      section:      parseInt(sec, 10),
    };
    if (!Number.isFinite(trsParsed.township_num) || !Number.isFinite(trsParsed.range_num)
        || !Number.isFinite(trsParsed.section) || trsParsed.section < 1 || trsParsed.section > 36) {
      setErr('TRS invalid — section must be 1-36'); return;
    }
    setBusy(true);
    try {
      await wyApi.addLead(props.wyOrderId, {
        name: name.trim(),
        trs: trsParsed,
        record_class: recordClass,
        city:  city.trim()  || null,
        state: state.trim() || null,
        tract_description:    tractDescription.trim() || null,
        interest_description: interestDescription.trim() || null,
      });
      props.onAdded();
    } catch (e: any) {
      setErr(e?.message ?? 'add failed'); setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={props.onClose}>
      <div className="bg-bg-card border border-border rounded-lg w-[640px] max-w-[94vw] max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-border flex items-center justify-between shrink-0">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">wyoming · add lead</div>
            <div className="font-display text-[22px] leading-tight">Add a working-interest lead</div>
          </div>
          <button onClick={props.onClose} className="text-text-dim hover:text-text text-2xl leading-none">×</button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Party name (e.g. SMITH JOHN A or ACME OIL LLC)"
            className="w-full bg-bg-input border border-border rounded px-3 py-2 text-[13px] outline-none focus:border-accent" />
          <div className="grid grid-cols-[1fr_80px] gap-2">
            <input value={city} onChange={e => setCity(e.target.value)} placeholder="City (mailing address)"
              className="bg-bg-input border border-border rounded px-3 py-2 text-[13px] outline-none focus:border-accent" />
            <input value={state} onChange={e => setState(e.target.value.toUpperCase().slice(0, 2))} placeholder="ST"
              maxLength={2}
              className="bg-bg-input border border-border rounded px-3 py-2 text-[13px] font-mono uppercase outline-none focus:border-accent" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-1">TRS</div>
            <div className="grid grid-cols-[1fr_60px_1fr_60px_1fr] gap-2">
              <input value={tn} onChange={e => setTn(e.target.value)} placeholder="T#"
                className="bg-bg-input border border-border rounded px-2 py-1.5 text-[12px] font-mono outline-none focus:border-accent" />
              <select value={td} onChange={e => setTd(e.target.value as 'N'|'S')}
                className="bg-bg-input border border-border rounded px-2 py-1.5 text-[12px] font-mono outline-none focus:border-accent">
                <option value="N">N</option><option value="S">S</option>
              </select>
              <input value={rn} onChange={e => setRn(e.target.value)} placeholder="R#"
                className="bg-bg-input border border-border rounded px-2 py-1.5 text-[12px] font-mono outline-none focus:border-accent" />
              <select value={rd} onChange={e => setRd(e.target.value as 'E'|'W')}
                className="bg-bg-input border border-border rounded px-2 py-1.5 text-[12px] font-mono outline-none focus:border-accent">
                <option value="W">W</option><option value="E">E</option>
              </select>
              <input value={sec} onChange={e => setSec(e.target.value)} placeholder="Sec"
                className="bg-bg-input border border-border rounded px-2 py-1.5 text-[12px] font-mono outline-none focus:border-accent" />
            </div>
          </div>
          <input value={tractDescription} onChange={e => setTractDescription(e.target.value)}
            placeholder="Tract description (optional sub-section text)"
            className="w-full bg-bg-input border border-border rounded px-3 py-2 text-[13px] outline-none focus:border-accent" />
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center gap-2 text-[12px] text-text-dim">
              <input type="radio" name="rc" checked={recordClass === 'working_interest'}
                onChange={() => setRecordClass('working_interest')} className="accent-accent" />
              Working interest
            </label>
            <label className="flex items-center gap-2 text-[12px] text-text-dim">
              <input type="radio" name="rc" checked={recordClass === 'interested_party'}
                onChange={() => setRecordClass('interested_party')} className="accent-accent" />
              Interested party
            </label>
          </div>
          {recordClass === 'working_interest' && (
            <input value={interestDescription} onChange={e => setInterestDescription(e.target.value)}
              placeholder='Interest description (e.g. "WI 0.0625" or "1/64 ORR" or "Working Interest 12.5%")'
              className="w-full bg-bg-input border border-border rounded px-3 py-2 text-[13px] outline-none focus:border-accent" />
          )}
          <div className="text-[10px] font-mono text-text-faint">
            Raw interest description is always preserved; decimal + type are parsed when recognizable.
          </div>
          {err && <div className="text-[12px] text-red bg-red/10 border border-red/40 rounded px-3 py-2">{err}</div>}
        </div>
        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 bg-bg-card-soft/40 shrink-0">
          <button onClick={props.onClose} disabled={busy}
            className="px-4 py-2 text-[13px] text-text-dim hover:text-text border border-border rounded disabled:opacity-50">Cancel</button>
          <button onClick={save} disabled={busy}
            className="px-4 py-2 text-[13px] bg-accent text-bg font-medium rounded hover:bg-accent-bright disabled:opacity-50">
            {busy ? 'Adding…' : 'Add lead'}
          </button>
        </div>
      </div>
    </div>
  );
}
